import React from "react";
import { Helmet } from "react-helmet";
import { useActiveLang } from '../../hooks/use-active-lang';
import { useEntity } from '../../hooks/use-entity';
import { useSite } from '../../hooks/use-site';
import { useSiteMetadata } from "../../hooks/use-site-metadata";
import { sprintf } from "sprintf-js";
import { truncate, filter, get } from "lodash";
import { coalesce } from "../../utils/coalesce";
import { entityLangPath } from "../../utils";
import { useLang } from "../../hooks/use-lang";
import { useSiteVerification } from "../../hooks/use-site-verification";
export var SeoDefaultProps = {
  title: null,
  description: null,
  image: null,
  article: false
};
export var Seo = function Seo(props) {
  var article = props.article;
  var metadata = useSiteMetadata();
  var titleTemplate = metadata.titleTemplate,
      image = metadata.image;
  var page = useEntity();
  var openGraph = page.openGraph,
      path = page.path;
  var t = useLang();
  var lang = useActiveLang();
  var site = useSite();
  var siteUrl = site.url || metadata.siteUrl; // try to grab a thumbnail first to avoid massive images

  var pageImage = coalesce(page, ['thumbnail.url', 'openGraph.image.thumbnails.large.url', 'openGraph.image.thumbnails.full.url', 'openGraph.image.url']) || site.thumbnail && site.thumbnail.url;
  var description = coalesce(page, ['description.plain', 'description.text', 'summary.plain', 'summary.text', 'intro.plain', 'intro.text', 'content.plain', 'content.text', 'openGraph.description.plain', 'openGraph.description.text']) || site.summary && site.summary.text || metadata.description;
  var title = site.title || metadata.title || site.name || '';
  var twitter = site.twitter || metadata.twitter;
  var pageTitle = get(page, 'title', '');
  var seo = {
    pageTitle: t(props.title) || pageTitle || page.name || t(title),
    title: t(props.title) || t(title),
    description: truncate(description, {
      length: 157,
      omission: '...'
    }),
    image: props.image || pageImage || image,
    url: "" + siteUrl + entityLangPath(page, lang)
  };
  var resolvedTitle = sprintf(t(titleTemplate), seo.pageTitle, seo.title);

  if (resolvedTitle === seo.pageTitle + " - " + seo.pageTitle) {
    resolvedTitle = seo.title;
  }

  if (process.env.DEBUG_SITE_VARS) {
    console.log("SEO: Site", site);
    console.log("SEO: Site Metadata", metadata);
    console.log("SEO: Page", page);
    console.log("SEO: Final Merged Info", seo);
  }

  var otherLanguages = filter(site.languages, function (language) {
    return language.iso !== lang.iso;
  });
  var SITE_VERIFICATION = useSiteVerification();
  var langIso = lang && lang.iso || 'en';
  return React.createElement(Helmet, {
    title: resolvedTitle,
    titleTemplate: "%s",
    htmlAttributes: {
      lang: langIso
    }
  }, otherLanguages.map(function (l) {
    return React.createElement("link", {
      key: l.locale,
      rel: "alternate",
      hrefLang: l.iso,
      href: entityLangPath(page, l)
    });
  }), React.createElement("meta", {
    name: "description",
    content: seo.description
  }), React.createElement("meta", {
    name: "image",
    content: seo.image
  }), SITE_VERIFICATION && React.createElement("meta", {
    name: "google-site-verification",
    content: SITE_VERIFICATION
  }), lang && lang.locale && React.createElement("meta", {
    property: "og:locale",
    content: lang.locale
  }), seo.url && React.createElement("meta", {
    property: "og:url",
    content: seo.url
  }), React.createElement("meta", {
    property: "og:type",
    content: article ? "article" : "website"
  }), React.createElement("meta", {
    property: 'og:updated_time',
    content: new Date().toISOString()
  }), seo.title && React.createElement("meta", {
    property: "og:title",
    content: resolvedTitle
  }), seo.description && React.createElement("meta", {
    property: "og:description",
    content: seo.description
  }), seo.image && React.createElement("meta", {
    property: "og:image",
    content: seo.image
  }), React.createElement("meta", {
    name: "twitter:card",
    content: "summary_large_image"
  }), twitter && React.createElement("meta", {
    name: "twitter:creator",
    content: twitter
  }), seo.title && React.createElement("meta", {
    name: "twitter:title",
    content: resolvedTitle
  }), seo.description && React.createElement("meta", {
    name: "twitter:description",
    content: seo.description
  }), seo.image && React.createElement("meta", {
    name: "twitter:image",
    content: seo.image
  }));
};